import api from './api';

export type CommChannel = 'EMAIL' | 'SMS';
export type CommDirection = 'INCOMING' | 'OUTGOING';

/** A file that travelled with a message, in either direction. The bytes come
 *  from downloadAttachment by id - the server never discloses the S3 key. */
export interface CommunicationAttachment {
  id: number;
  file_name: string;
  file_type: string;
  file_size: number;
}

export interface Communication {
  id: number;
  patient_id: number | null;
  /** Set when the message was exchanged with a lead rather than a patient. */
  lead_id?: number | null;
  tenant_id: number | null;
  channel: CommChannel;
  direction: CommDirection;
  status: string;
  source: string;
  subject: string | null;
  body_text: string | null;
  body_html: string | null;
  from_address: string | null;
  to_address: string | null;
  created_by: number | null;
  created_at: string;
  received_at: string | null;
  /** Optional rather than required: a browser running this bundle against an
   *  older API gets a response without the field, and should show no
   *  attachments rather than crash on undefined. */
  attachments?: CommunicationAttachment[];
}

export interface ComposeEmailPayload {
  subject: string;
  body_html: string;
  /** Picked in the compose box. Sent as multipart when present, which is also
   *  the only shape that can carry them. */
  attachments?: globalThis.File[];
  /** Which of the record's addresses to send to. Omitted means their own, so
   *  the request is unchanged for anyone who does not touch the picker. */
  to_email?: string;
}

/** One address a composed email may be sent to.
 *
 *  Served by the API rather than read off the patient object: the addresses
 *  past the first live in tenant-configured fields the list screens do not
 *  load, and this is the same list the send is validated against - so the
 *  picker cannot offer something the send would refuse.
 */
export interface EmailRecipient {
  email: string;
  /** The field's configured label, e.g. "GP's Email Address". */
  label: string;
  /** 'primary' is the record's own address; anything else is a third party. */
  source: 'primary' | 'additional';
}

export interface CommunicationListResponse {
  items: Communication[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface CommunicationFilters {
  channel?: CommChannel;
  direction?: CommDirection;
  page?: number;
  page_size?: number;
}

/** The compose body in the shape the API wants.
 *
 *  Plain JSON when there is nothing attached, multipart when there is. Keeping
 *  the JSON path means a send with no attachments is byte-for-byte the request
 *  this app has always made, so the common case cannot be broken by this
 *  feature - and the API accepts either, so neither side has to deploy first.
 */
const composeBody = (data: ComposeEmailPayload): { body: unknown; config?: object } => {
  if (!data.attachments?.length) {
    return {
      body: {
        subject: data.subject,
        body_html: data.body_html,
        ...(data.to_email ? { to_email: data.to_email } : {}),
      },
    };
  }
  const form = new FormData();
  form.append('subject', data.subject);
  form.append('body_html', data.body_html);
  if (data.to_email) form.append('to_email', data.to_email);
  data.attachments.forEach((file) => form.append('attachments', file));
  return {
    body: form,
    config: { headers: { 'Content-Type': 'multipart/form-data' } },
  };
};

export const communicationService = {
  async getPatientCommunications(
    patientId: number,
    filters: CommunicationFilters = {},
  ): Promise<CommunicationListResponse> {
    const params = new URLSearchParams();
    if (filters.channel) params.append('channel', filters.channel);
    if (filters.direction) params.append('direction', filters.direction);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.page_size) params.append('page_size', String(filters.page_size));
    const response = await api.get(
      `/patients/${patientId}/communications?${params.toString()}`,
    );
    return response.data;
  },

  /** The addresses this patient's email may go to, their GP's included. */
  async getEmailRecipients(patientId: number): Promise<EmailRecipient[]> {
    const response = await api.get(
      `/patients/${patientId}/communications/email-recipients`,
    );
    return response.data;
  },

  async sendEmail(
    patientId: number,
    data: ComposeEmailPayload,
  ): Promise<Communication> {
    const { body, config } = composeBody(data);
    const response = await api.post(
      `/patients/${patientId}/communications/email`,
      body,
      config,
    );
    return response.data;
  },

  /** A lead's timeline. Same shape as the patient one, so the same panel
   *  renders it unchanged. */
  async getLeadCommunications(
    leadId: number,
    filters: CommunicationFilters = {},
  ): Promise<CommunicationListResponse> {
    const params = new URLSearchParams();
    if (filters.channel) params.append('channel', filters.channel);
    if (filters.direction) params.append('direction', filters.direction);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.page_size) params.append('page_size', String(filters.page_size));
    const response = await api.get(
      `/leads/${leadId}/communications?${params.toString()}`,
    );
    return response.data;
  },

  /** The addresses this lead's email may go to. */
  async getLeadEmailRecipients(leadId: number): Promise<EmailRecipient[]> {
    const response = await api.get(`/leads/${leadId}/communications/email-recipients`);
    return response.data;
  },

  /** Signature-identical to sendEmail on purpose: ComposeEmailDialog picks one
   *  of the two by reference, so they have to stay interchangeable. */
  async sendLeadEmail(
    leadId: number,
    data: ComposeEmailPayload,
  ): Promise<Communication> {
    const { body, config } = composeBody(data);
    const response = await api.post(
      `/leads/${leadId}/communications/email`,
      body,
      config,
    );
    return response.data;
  },

  /** Save an attachment to disk. Streamed through the API rather than opened
   *  from S3 directly, so the download stays behind the bearer token; the
   *  filename comes back in Content-Disposition (RFC 5987 for non-ASCII). */
  async downloadAttachment(
    communicationId: number,
    attachmentId: number,
    fallbackName = 'attachment',
  ): Promise<void> {
    const response = await api.get(
      `/communications/${communicationId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' },
    );
    const disposition = response.headers['content-disposition'];
    let fileName = fallbackName;
    if (disposition) {
      const encoded = disposition.match(/filename\*=UTF-8''(.+)/);
      if (encoded?.[1]) {
        fileName = decodeURIComponent(encoded[1]);
      } else {
        const plain = disposition.match(/filename="?(.+?)"?$/);
        if (plain?.[1]) fileName = plain[1];
      }
    }
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  async getUnmatched(
    filters: { page?: number; page_size?: number } = {},
  ): Promise<CommunicationListResponse> {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', String(filters.page));
    if (filters.page_size) params.append('page_size', String(filters.page_size));
    const response = await api.get(`/communications/unmatched?${params.toString()}`);
    return response.data;
  },

  async assignToLead(communicationId: number, leadId: number): Promise<Communication> {
    const response = await api.post(`/communications/${communicationId}/assign`, {
      lead_id: leadId,
    });
    return response.data;
  },

  async assignToPatient(communicationId: number, patientId: number): Promise<Communication> {
    const response = await api.post(`/communications/${communicationId}/assign`, {
      patient_id: patientId,
    });
    return response.data;
  },
};
